import type { LucideIcon } from "lucide-react";
import {
  BookOpen,
  CheckCircle,
  ClipboardCheck,
  CreditCard,
  FileText,
  Landmark,
  LayoutDashboard,
  Link2,
  ListTree,
  Wallet,
} from "lucide-react";

export type AccountingSectionId =
  | "dashboard"
  | "review"
  | "journal_approvals"
  | "fund_grant"
  | "budget"
  | "ar"
  | "ap"
  | "gl"
  | "bank_rec"
  | "payroll"
  | "reporting"
  | "posting_rules"
  | "coa"
  | "integrations";

export interface AccountingSubTab {
  key: string;
  label: string;
  description?: string;
}

export interface AccountingSection {
  id: AccountingSectionId;
  label: string;
  icon: LucideIcon;
  subTabs?: AccountingSubTab[];
  /** Section manages its own sub-navigation (no outer tab bar). */
  internalNav?: boolean;
  badgeKey?: "pending_reviews" | "pending_journal_approvals" | "failed_syncs";
}

export const ACCOUNTING_SECTIONS: AccountingSection[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: LayoutDashboard,
  },
  {
    id: "review",
    label: "Accounting Review",
    icon: ClipboardCheck,
    internalNav: true,
    badgeKey: "pending_reviews",
  },
  {
    id: "journal_approvals",
    label: "Journal Entry Approvals",
    icon: CheckCircle,
    internalNav: true,
    badgeKey: "pending_journal_approvals",
  },
  {
    id: "fund_grant",
    label: "Fund & Grant",
    icon: Landmark,
    subTabs: [
      {
        key: "funds",
        label: "Funds",
        description: "Accounting and funding classification (restriction, GL, balances)",
      },
      {
        key: "grants",
        label: "Grants",
        description: "Grant opportunities, applications, and awards",
      },
      {
        key: "grant_budgets",
        label: "Grant Budgets",
        description: "Grant budget vs actual",
      },
      // {
      //   key: "transfers",
      //   label: "Transfers",
      //   description: "Bank account movements (e.g. operating to savings), not income or expense",
      // },
      // {
      //   key: "releases",
      //   label: "Releases",
      //   description: "Restricted-fund releases",
      // },
      {
        key: "contributions",
        label: "Contributions",
        description: "Record gifts (Draft); posting happens in Accounting Review",
      },
      {
        key: "pledges",
        label: "Pledges",
        description: "Donations promised but not yet received",
      },
    ],
  },
  // Temporarily hidden from accounting menu (pilot).
  // {
  //   id: "budget",
  //   label: "Budget & Forecasting",
  //   icon: Scale,
  //   subTabs: [
  //     { key: "setup", label: "Budgets" },
  //     { key: "forecasts", label: "Forecasts" },
  //     { key: "adjustments", label: "Adjustments" },
  //   ],
  // },
  {
    id: "ar",
    label: "Accounts Receivable",
    icon: FileText,
    subTabs: [
      { key: "customers", label: "Customers" },
      { key: "invoices", label: "Invoices" },
      { key: "payments", label: "Payments" },
      { key: "credits", label: "Credit Notes" },
      { key: "statements", label: "Statements" },
      { key: "aging", label: "Aging" },
    ],
  },
  {
    id: "ap",
    label: "Accounts Payable",
    icon: CreditCard,
    subTabs: [
      { key: "vendors", label: "Vendors" },
      { key: "bills", label: "Bills" },
      { key: "payments", label: "Payments" },
      { key: "aging", label: "AP Aging" },
    ],
  },
  {
    id: "gl",
    label: "General Ledger",
    icon: BookOpen,
    subTabs: [
      { key: "ledger", label: "Journal Entries" },
      { key: "activity", label: "Account Activity" },
      { key: "trial_balance", label: "Trial Balance" },
      { key: "search", label: "Transaction Search" },
    ],
  },
  // Temporarily hidden from accounting menu (pilot).
  // {
  //   id: "bank_rec",
  //   label: "Bank Reconciliation",
  //   icon: Landmark,
  //   subTabs: [
  //     { key: "reconciliations", label: "Reconciliations" },
  //     { key: "matching", label: "Matching" },
  //     { key: "exceptions", label: "Exceptions" },
  //     { key: "completed", label: "Completed" },
  //     { key: "close", label: "Period Close" },
  //   ],
  // },
  // {
  //   id: "payroll",
  //   label: "Payroll & Labor",
  //   icon: Clock,
  //   subTabs: [
  //     { key: "timesheets", label: "Timesheets" },
  //     { key: "payroll_review", label: "Payroll Review" },
  //     { key: "labor_allocation", label: "Labor Allocation" },
  //     { key: "rates", label: "Rates" },
  //   ],
  // },
  {
    id: "reporting",
    label: "Financial Reporting",
    icon: Wallet,
    internalNav: true,
  },
  {
    id: "posting_rules",
    label: "Posting Rules",
    icon: BookOpen,
    internalNav: true,
  },
  {
    id: "coa",
    label: "Chart of Accounts",
    icon: ListTree,
    subTabs: [
      { key: "list", label: "Account List" },
      { key: "categories", label: "Categories" },
      { key: "qbo_mapping", label: "QBO Mapping" },
    ],
  },
  {
    id: "integrations",
    label: "Integrations",
    icon: Link2,
    subTabs: [
      { key: "quickbooks", label: "QuickBooks" },
      { key: "billcom", label: "Bill.com" },
      { key: "sync_history", label: "Sync History" },
      { key: "error_log", label: "Error Log" },
    ],
    badgeKey: "failed_syncs",
  },
];

/** Legacy flat tabs that now live at /submission-center. */
export const SUBMISSION_CENTER_LEGACY_TABS = new Set(["submit", "expenses"]);

export function submissionCenterPathForLegacyTab(tab: string): string | null {
  if (tab === "expenses") return "/submission-center?tab=expenses";
  if (tab === "submit") return "/submission-center";
  return null;
}

/** Legacy `?tab=` keys from the flat horizontal nav (pre-restructure). */
export const LEGACY_TAB_MAP: Record<
  string,
  { section: AccountingSectionId; tab?: string; reviewSubTab?: string }
> = {
  overview: { section: "dashboard" },
  proposals: { section: "review", reviewSubTab: "pending" },
  exceptions: { section: "review", reviewSubTab: "exceptions" },
  funds: { section: "fund_grant", tab: "funds" },
  campaigns: { section: "fund_grant", tab: "funds" },
  transfers: { section: "fund_grant", tab: "transfers" },
  releases: { section: "fund_grant", tab: "releases" },
  contributions: { section: "fund_grant", tab: "contributions" },
  pledges: { section: "fund_grant", tab: "pledges" },
  budgets: { section: "budget", tab: "setup" },
  customers: { section: "ar", tab: "customers" },
  invoices: { section: "ar", tab: "invoices" },
  credit_notes: { section: "ar", tab: "credits" },
  vendors: { section: "ap", tab: "vendors" },
  bills: { section: "ap", tab: "bills" },
  journals: { section: "gl", tab: "ledger" },
  accounts: { section: "coa", tab: "list" },
  bank_rec: { section: "bank_rec", tab: "reconciliations" },
  close: { section: "bank_rec", tab: "close" },
  timesheets: { section: "payroll", tab: "timesheets" },
  reports: { section: "reporting" },
  posting_rules: { section: "posting_rules" },
  quickbooks: { section: "integrations", tab: "quickbooks" },
  billcom: { section: "integrations", tab: "billcom" },
};

const ALL_SECTION_IDS = ACCOUNTING_SECTIONS.map((s) => s.id);

/** Coding, approval, and posting stay with the accountant. Owners can view the rest. */
const OWNER_HIDDEN_SECTIONS = new Set<AccountingSectionId>([
  "review",
  "journal_approvals",
  "posting_rules",
]);

export function getVisibleSectionIds(
  hasMinRole: (minimum: string) => boolean,
  role: string,
): AccountingSectionId[] {
  if (role === "OWNER") {
    return ALL_SECTION_IDS.filter((id) => !OWNER_HIDDEN_SECTIONS.has(id));
  }
  if (hasMinRole("ACCOUNTANT") || role === "ADMIN") {
    return ALL_SECTION_IDS;
  }
  if (role === "BOARD") {
    return ["dashboard", "reporting"];
  }
  if (hasMinRole("STAFF")) {
    // Restore "payroll" when Payroll & Labor is back in ACCOUNTING_SECTIONS.
    return ["dashboard"];
  }
  return ["dashboard"];
}

export function getVisibleSubTabs(section: AccountingSection): AccountingSubTab[] {
  return section.subTabs ?? [];
}

export function getSectionById(id: AccountingSectionId): AccountingSection | undefined {
  return ACCOUNTING_SECTIONS.find((s) => s.id === id);
}

export function defaultSubTab(section: AccountingSection): string | undefined {
  return section.subTabs?.[0]?.key;
}

export function resolveNavigationFromSearchParams(
  searchParams: URLSearchParams,
  visibleSectionIds: AccountingSectionId[],
): {
  section: AccountingSectionId;
  tab?: string;
  reviewSubTab?: string;
} {
  let section = searchParams.get("section") as AccountingSectionId | null;
  let tab = searchParams.get("tab") ?? undefined;
  let reviewSubTab = searchParams.get("reviewTab") ?? undefined;

  const legacyTab = searchParams.get("legacyTab");
  if (!section && legacyTab) {
    const mapped = LEGACY_TAB_MAP[legacyTab];
    if (mapped) {
      section = mapped.section;
      tab = mapped.tab ?? tab;
      reviewSubTab = mapped.reviewSubTab ?? reviewSubTab;
    }
  }

  if (!section) {
    const oldTabOnly = searchParams.get("tab");
    if (oldTabOnly && LEGACY_TAB_MAP[oldTabOnly]) {
      const mapped = LEGACY_TAB_MAP[oldTabOnly];
      section = mapped.section;
      tab = mapped.tab ?? undefined;
      reviewSubTab = mapped.reviewSubTab ?? reviewSubTab;
    }
  }

  if (!section || !visibleSectionIds.includes(section)) {
    section = visibleSectionIds[0] ?? "dashboard";
  }

  const sectionDef = getSectionById(section);
  if (sectionDef?.subTabs?.length) {
    const visibleSubs = sectionDef.subTabs;
    if (!tab || !visibleSubs.some((t) => t.key === tab)) {
      tab = defaultSubTab(sectionDef);
    }
  } else if (section === "reporting") {
    const reportTabs = [
      "position",
      "activities",
      "fund_balances",
      "cash_flow",
      "functional_expenses",
      "budget_vs_actual",
      "trial_balance",
      "transaction_search",
    ];
    if (!tab || !reportTabs.includes(tab)) tab = "position";
  } else {
    tab = undefined;
  }

  return { section, tab, reviewSubTab };
}

export function buildAccountingSearchParams(
  section: AccountingSectionId,
  tab?: string,
  reviewSubTab?: string,
): Record<string, string> {
  const params: Record<string, string> = { section };
  if (tab) params.tab = tab;
  if (reviewSubTab) params.reviewTab = reviewSubTab;
  return params;
}

/** Icons used only in legacy map keys for completeness tests. */
export const LEGACY_TAB_KEYS = Object.keys(LEGACY_TAB_MAP);
